import { ApiError } from "../../api/apiError";
import { requestErrorText } from "../queue/requestErrorText";
import type { TFunction } from "../queue/requestErrorText";
import { isRefusalCode } from "./api";

// Подпись предиката гейта; незнакомый id (например, ступень перед
// слиянием) показывается как есть.
export function predicateLabel(id: string, t: TFunction): string {
  return t(`mergeApproval.predicate.${id}`, { defaultValue: id });
}

interface FailedItem {
  readonly id: string;
  readonly predicateClass: string;
}

// Элементы `failed` с строковыми `id` и `predicate_class`; `reason` не читается.
function failedPredicates(
  data: Readonly<Record<string, unknown>>,
): FailedItem[] {
  const failed = data.failed;
  if (!Array.isArray(failed)) {
    return [];
  }
  return failed.flatMap((item: unknown): FailedItem[] => {
    if (typeof item !== "object" || item === null) {
      return [];
    }
    const { id, predicate_class: predicateClass } = item as Record<
      string,
      unknown
    >;
    return typeof id === "string" && typeof predicateClass === "string"
      ? [{ id, predicateClass }]
      : [];
  });
}

function labels(items: readonly FailedItem[], t: TFunction): string {
  return items.map((item) => predicateLabel(item.id, t)).join(", ");
}

function gateBlockedText(
  data: Readonly<Record<string, unknown>>,
  t: TFunction,
): string {
  const failed = failedPredicates(data);
  const acceptable = failed.filter((f) => f.predicateClass === "acceptable");
  const prohibiting = failed.filter((f) => f.predicateClass !== "acceptable");
  const parts = [t("mergeApproval.refusal.gate_blocked")];
  if (prohibiting.length > 0) {
    parts.push(
      t("mergeApproval.refusal.gate_blocked_prohibiting", {
        predicates: labels(prohibiting, t),
      }),
    );
  }
  if (acceptable.length > 0) {
    parts.push(
      t("mergeApproval.refusal.gate_blocked_acceptable", {
        predicates: labels(acceptable, t),
      }),
    );
  }
  return parts.join(" ");
}

function mergeRefusedText(
  data: Readonly<Record<string, unknown>>,
  t: TFunction,
): string {
  const { host_message: hostMessage, host_status: hostStatus } = data;
  if (typeof hostMessage === "string" && hostMessage.trim() !== "") {
    return t("mergeApproval.refusal.merge_refused_host", { hostMessage });
  }
  if (typeof hostStatus === "number") {
    return t("mergeApproval.refusal.merge_refused_status", { hostStatus });
  }
  return t("mergeApproval.refusal.merge_refused");
}

// Текст отказа по машиночитаемому `code` (ADR-0091 § 5, § 7); прочее — общий
// текст по статусу или «нет связи». `message` сервера не выводится.
export function refusalText(error: unknown, t: TFunction): string {
  if (error instanceof ApiError && error.code !== null) {
    if (isRefusalCode(error.code)) {
      if (error.code === "gate_blocked") {
        return gateBlockedText(error.data, t);
      }
      if (error.code === "merge_refused") {
        return mergeRefusedText(error.data, t);
      }
      return t(`mergeApproval.refusal.${error.code}`);
    }
    if (error.code === "permission_denied") {
      return t("mergeApproval.refusal.permission_denied");
    }
  }
  return requestErrorText(error, t);
}
